import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AppLayout, LoadingSpinner } from '../components/Layout';
import {
  Search, DollarSign, TrendingUp, Users, Bookmark, ArrowRight, X,
  Compass, CheckCircle2, Target, Sparkles, GitCompare, Brain, GraduationCap,
  Award, BarChart3, Rocket, Star, Zap, Loader2,
} from 'lucide-react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { C, GLOW, GRAD, FIT, fitBucket } from './careerTokens';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const stagger = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.05 } } };
const fadeUp = { hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

const CHIPS = [
  { id: 'all', label: 'All Careers', computable: true },
  { id: 'best', label: 'Best Fit for Me', computable: true },
  { id: 'growth', label: 'High Growth', computable: true },
  { id: 'pay', label: '$70K+', computable: true },
  { id: 'major', label: 'Matches My Major', computable: true },
  { id: 'remote', label: 'Remote Friendly', computable: false },
  { id: 'creative', label: 'Creative', computable: false },
  { id: 'people', label: 'People Focused', computable: false },
];

const parseLow = (s) => parseInt((String(s || '').match(/[\d,]+/g)?.[0] || '0').replace(/,/g, ''), 10);
const parseGrowth = (s) => parseInt(String(s || '').replace(/[^\d-]/g, '') || '0', 10);

export default function CareerLandscapePage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const authH = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [student, setStudent] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [unlocks, setUnlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [activeChip, setActiveChip] = useState('all');
  const [saved, setSaved] = useState([]);
  const [compare, setCompare] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [currentTarget, setCurrentTarget] = useState('');
  const [settingTarget, setSettingTarget] = useState(false);
  const [confirmChange, setConfirmChange] = useState(null);

  // search
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [research, setResearch] = useState(null); // researching a searched career (not a target switch)

  // Open a searched career for RESEARCH, never auto-switches the student's target.
  const openResearch = (r) => {
    setQuery('');
    const t = (r.title || '').toLowerCase();
    const node = nodes.find(n => {
      const nt = n.title.toLowerCase();
      return nt === t || nt.includes(t) || t.includes(nt);
    });
    if (node) { setResearch(null); setSelected(node); }
    else { setResearch(r); }
  };

  useEffect(() => {
    if (!token) return;
    Promise.all([
      axios.get(`${API}/college/career-landscape`, authH),
      axios.get(`${API}/career/target`, authH).catch(() => ({ data: {} })),
    ]).then(([land, tgt]) => {
      const ns = land.data.nodes || [];
      setNodes(ns);
      setUnlocks(land.data.unlocks || []);
      setStudent(land.data.student);
      setCurrentTarget(tgt.data?.target?.title || land.data.student?.career_target || '');
      setSelected(ns.find(n => (tgt.data?.target?.title || '').toLowerCase() === n.title.toLowerCase()) || ns[0] || null);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [token, authH]);

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    const t = setTimeout(() => {
      axios.get(`${API}/career/occupations?q=${encodeURIComponent(query)}&limit=8`, authH)
        .then(r => setResults(r.data.occupations || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(t);
  }, [query, authH]);

  const toggleCompare = useCallback((node) => {
    setCompare(prev => {
      if (prev.find(n => n.id === node.id)) return prev.filter(n => n.id !== node.id);
      if (prev.length >= 3) { toast.error('Compare up to 3 careers'); return prev; }
      return [...prev, node];
    });
  }, []);

  const toggleSave = (node) => {
    setSaved(prev => prev.includes(node.id)
      ? (toast.success(`Removed ${node.title}`), prev.filter(p => p !== node.id))
      : (toast.success(`Saved ${node.title}`), [...prev, node.id]));
  };

  const setAsTarget = async (title, confirm = false) => {
    setSettingTarget(true);
    try {
      const res = await axios.post(`${API}/career/target`, { title, source: 'explore', confirm }, authH);
      if (res.data.requires_confirmation) { setConfirmChange({ ...res.data, title }); setSettingTarget(false); return; }
      setConfirmChange(null);
      setCurrentTarget(res.data.target?.title || title);
      toast.success(`Locked on ${res.data.target?.title || title}. Opening your Career Plan…`);
      setTimeout(() => navigate('/app/career-intelligence'), 700);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === 'unverified_occupation') toast.error(d.message);
      else toast.error('Could not set that target. Try the closest verified title in search.');
      setSettingTarget(false);
    }
  };

  const isTarget = (node) => node && currentTarget && node.title.toLowerCase() === currentTarget.toLowerCase();

  const recommended = useMemo(() => {
    let list = [...nodes];
    if (activeChip === 'best') list.sort((a, b) => b.fit - a.fit);
    else if (activeChip === 'growth') list = list.filter(n => parseGrowth(n.outlook) >= 10);
    else if (activeChip === 'pay') list = list.filter(n => parseLow(n.salary) >= 70000);
    else if (activeChip === 'major') list = list.filter(n => n.ring === 0);
    return list.slice(0, 4);
  }, [nodes, activeChip]);

  if (loading) return <AppLayout><div className="flex items-center justify-center min-h-[60vh]"><LoadingSpinner size="lg" /></div></AppLayout>;

  const initials = (student?.name || 'You').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <AppLayout>
      <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-4 max-w-[1440px] mx-auto" data-testid="explore-careers-page">

        {/* HEADER (compact) */}
        <motion.div variants={fadeUp} className="relative overflow-hidden rounded-2xl px-6 py-4"
          style={{ background: GRAD.hero, border: `1px solid ${C.border}55` }}>
          <div aria-hidden className="pointer-events-none absolute -top-24 -right-16 w-72 h-72 rounded-full blur-3xl opacity-20"
            style={{ background: `radial-gradient(circle, ${C.blue}, transparent 65%)` }} />
          <div className="relative flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-outfit font-extrabold text-white tracking-tight" style={{ fontSize: 'clamp(24px,2.6vw,32px)' }}>
                Explore <span style={{ color: C.cyan }}>Careers</span>
              </h1>
              <p className="text-[13px] mt-0.5" style={{ color: C.text2 }}>See where your degree, interests, strengths, and goals could take you.</p>
            </div>
            <div className="text-right hidden md:block">
              <p className="font-outfit font-bold leading-tight" style={{ color: C.cyan, fontSize: 16 }}>Your future has more paths than you think.</p>
            </div>
          </div>

          {/* SEARCH */}
          <div className="relative mt-5 max-w-3xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: C.muted }} />
            <input value={query} onChange={e => setQuery(e.target.value)} data-testid="career-search-input"
              placeholder="Search any career, field, or interest..."
              className="w-full rounded-2xl pl-12 pr-4 py-3.5 text-[15px] text-white placeholder:text-slate-300 outline-none"
              style={{ background: C.surface, border: `1px solid ${C.border}` }} />
            <AnimatePresence>
              {query.trim().length >= 2 && (
                <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                  className="absolute z-30 mt-2 w-full rounded-xl overflow-hidden"
                  style={{ background: C.surface2, border: `1px solid ${C.border}`, boxShadow: GLOW.hero }}
                  data-testid="career-search-results">
                  {searching && <div className="px-4 py-3 text-sm flex items-center gap-2" style={{ color: C.muted }}><Loader2 className="w-4 h-4 animate-spin" /> Searching verified careers…</div>}
                  {!searching && results.length === 0 && <div className="px-4 py-3 text-sm" style={{ color: C.muted }}>No verified careers match “{query}”.</div>}
                  {!searching && results.map((r, i) => (
                    <button key={i} onClick={() => openResearch(r)} data-testid={`search-result-${i}`}
                      className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-white/[0.05] transition-colors">
                      <span className="text-sm text-white">{r.title}</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ color: C.cyan, background: `${C.cyan}18` }}>Research</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
            <p className="text-[11px] mt-2" style={{ color: C.muted }}>Search to research any path, we'll show it, and you decide if it becomes your target.</p>
          </div>

          {/* FILTER CHIPS */}
          <div className="flex flex-wrap gap-2 mt-4">
            {CHIPS.map(chip => {
              const active = activeChip === chip.id;
              return (
              <button key={chip.id} data-testid={`chip-${chip.id}`} aria-pressed={active}
                onClick={() => chip.computable ? setActiveChip(chip.id) : toast.message('Add profile tags to use this filter.')}
                className="text-[12px] px-3.5 py-1.5 rounded-full font-semibold transition-all duration-200"
                style={active
                  ? { background: GRAD.cta, color: '#021735', border: `1px solid ${C.cyan}`, boxShadow: `0 0 14px ${C.cyan}66`, transform: 'translateY(-1px)' }
                  : { background: 'rgba(255,255,255,0.03)', color: chip.computable ? C.text2 : C.muted, border: `1px solid ${C.border}55` }}>
                {chip.label}
              </button>
              );
            })}
          </div>
        </motion.div>

        {/* MAP + PREVIEW */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
          {/* CONSTELLATION */}
          <motion.div variants={fadeUp} className="xl:col-span-8">
            <div className="relative overflow-hidden rounded-2xl p-6"
              style={{ background: C.surface, border: `1px solid ${C.border}66`, minHeight: 660 }}>
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-outfit font-bold text-white text-xl flex items-center gap-2"><Compass className="w-5 h-5" style={{ color: C.cyan }} /> Your Career Landscape</h2>
                <div className="flex items-center gap-3 text-[11px]">
                  {Object.values(FIT).map(f => (
                    <span key={f.label} className="flex items-center gap-1.5" style={{ color: C.text2 }}>
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: f.color, boxShadow: `0 0 6px ${f.color}88` }} />{f.label}
                    </span>
                  ))}
                </div>
              </div>
              <p className="text-[12px] mb-2" style={{ color: C.muted }}>Distance from center = how close each path is to your current background. Closer means stronger fit.</p>

              <div className="relative mx-auto" style={{ width: '100%', height: 580 }}>
                <div className="absolute inset-0 flex items-center justify-center">
                  {[580, 420, 250].map((s, ri) => (
                    <motion.div key={s} className="rounded-full absolute"
                      style={{ width: s, height: s, border: `1px solid ${C.border}88` }}
                      animate={{ opacity: [0.55, 0.85, 0.55] }}
                      transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: ri * 0.6 }} />
                  ))}
                </div>
                {/* Student center */}
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 text-center">
                  <motion.div className="w-[86px] h-[86px] mx-auto rounded-full flex items-center justify-center text-white text-xl font-bold"
                    style={{ background: GRAD.cta }}
                    animate={{ boxShadow: [`0 0 26px ${C.cyan}55`, `0 0 44px ${C.cyan}99`, `0 0 26px ${C.cyan}55`] }}
                    transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}>{initials}</motion.div>
                  <p className="text-[13px] text-white font-semibold mt-2">{student?.name?.split(' ')[0] || 'You'}</p>
                  <p className="text-[10px]" style={{ color: C.muted }}>{student?.major || student?.career_target}</p>
                  <p className="text-[10px] capitalize" style={{ color: C.muted }}>{student?.academic_year}</p>
                </div>
                {/* Nodes */}
                {nodes.map((node, ni) => {
                  const bucket = fitBucket(node); const fc = FIT[bucket].color;
                  const isSel = selected?.id === node.id;
                  const radius = node.ring === 0 ? 148 : node.ring === 1 ? 224 : 288;
                  const rad = (node.angle * Math.PI) / 180;
                  const x = Math.cos(rad) * radius, y = Math.sin(rad) * radius;
                  return (
                    <div key={node.id} onClick={() => { setResearch(null); setSelected(node); }} data-testid={`node-${node.id}`}
                      className="absolute cursor-pointer transition-transform duration-300 hover:scale-[1.08]"
                      style={{ left: `calc(50% + ${x}px - 54px)`, top: `calc(50% + ${y}px - 22px)`, zIndex: isSel ? 20 : 5, transform: isSel ? 'scale(1.08)' : undefined }}>
                      <svg className="absolute pointer-events-none" style={{ left: 54, top: 22, width: 1, height: 1, overflow: 'visible' }}>
                        <line x1="0" y1="0" x2={-x} y2={-y} stroke={fc} strokeWidth="1" opacity="0.15" />
                      </svg>
                      <div className="rounded-xl px-3 py-1.5 text-center" style={{
                        minWidth: 112,
                        background: isSel ? `${fc}1F` : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${isSel ? fc : 'rgba(255,255,255,0.08)'}`,
                        boxShadow: isTarget(node) ? `0 0 0 1px ${C.cyan}, 0 0 16px ${C.cyan}55` : isSel ? `0 0 18px -4px ${fc}88` : 'none',
                      }}>
                        <div className="flex items-center justify-center gap-1">
                          <motion.span className="w-2 h-2 rounded-full" style={{ background: fc }}
                            animate={{ scale: [1, 1.5, 1], boxShadow: [`0 0 4px ${fc}66`, `0 0 10px ${fc}`, `0 0 4px ${fc}66`] }}
                            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut', delay: (ni % 6) * 0.35 }} />
                          <p className="text-[11px] text-white font-semibold leading-tight">{node.title}</p>
                        </div>
                        {typeof node.fit === 'number' && (
                          <p className="text-[10px] font-bold leading-tight" style={{ color: fc }} data-testid={`node-fit-${node.id}`}>{node.fit}% fit</p>
                        )}
                        {isTarget(node) && <p className="text-[8px] font-bold uppercase tracking-wider" style={{ color: C.cyan }}>Current Target</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* PREVIEW */}
          <motion.div variants={fadeUp} className="xl:col-span-4">
            <AnimatePresence mode="wait">
              {research ? (
                <motion.div key={`research-${research.title}`} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }} transition={{ duration: 0.22 }}>
                  <div className="rounded-2xl p-5 sticky top-4" style={{ background: C.surface, border: `1px solid ${C.purple}66`, boxShadow: GLOW.hero }} data-testid="research-preview">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                      style={{ color: C.purple, background: `${C.purple}1A`, border: `1px solid ${C.purple}44` }}>
                      <Search className="w-3 h-3" /> Researching
                    </span>
                    <h3 className="font-outfit font-bold text-white text-xl mt-2">{research.title}</h3>
                    {research.field && <p className="text-[12px] mt-1" style={{ color: C.text2 }}>Field: {research.field}</p>}
                    <p className="text-[12.5px] mt-3 leading-relaxed" style={{ color: C.muted }}>
                      You're just exploring this path, your target stays <span className="text-white font-semibold">{currentTarget || 'unchanged'}</span>. Make it your target only when you're ready, and we'll build a full Career Plan (salary, skills, employers and your route).
                    </p>
                    <div className="mt-5 space-y-2">
                      <button onClick={() => setAsTarget(research.title)} disabled={settingTarget} data-testid="research-set-target"
                        className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-sm font-bold text-white disabled:opacity-60"
                        style={{ background: GRAD.cta, boxShadow: GLOW.cyan }}>
                        {settingTarget ? <Loader2 className="w-4 h-4 animate-spin" /> : <Target className="w-4 h-4" />} Make This My Target
                      </button>
                      <button onClick={() => setResearch(null)} data-testid="research-back"
                        className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-xs font-semibold"
                        style={{ color: C.text2, border: `1px solid ${C.border}66` }}>
                        <Compass className="w-3.5 h-3.5" /> Back to my map
                      </button>
                    </div>
                  </div>
                </motion.div>
              ) : selected ? (
                <motion.div key={selected.id} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }} transition={{ duration: 0.22 }}>
                  <div className="rounded-2xl p-5 sticky top-4" style={{ background: C.surface, border: `1px solid ${C.cyan}55`, boxShadow: GLOW.hero }} data-testid="career-preview">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                        style={{ color: FIT[fitBucket(selected)].color, background: `${FIT[fitBucket(selected)].color}1A`, border: `1px solid ${FIT[fitBucket(selected)].color}44` }}
                        data-testid="preview-fit">
                        {isTarget(selected) ? 'Current Target' : FIT[fitBucket(selected)].label}
                        {typeof selected.fit === 'number' && ` · ${selected.fit}% fit`}
                      </span>
                      <button onClick={() => toggleSave(selected)} className="text-slate-300 hover:text-white" data-testid="preview-save">
                        <Bookmark className="w-4 h-4" style={saved.includes(selected.id) ? { color: C.cyan, fill: C.cyan } : {}} />
                      </button>
                    </div>
                    <h3 className="font-outfit font-bold text-white text-xl">{selected.title}</h3>
                    <p className="text-[12.5px] mt-1 leading-relaxed" style={{ color: C.text2 }}>{selected.description}</p>

                    <div className="grid grid-cols-3 gap-2 mt-4">
                      <Stat icon={DollarSign} color={C.green} label="Early Career" value={selected.salary} />
                      <Stat icon={TrendingUp} color={C.blue} label="Job Growth" value={selected.outlook} sub={selected.outlookLabel} />
                      <Stat icon={Users} color={C.gold} label="Competition" value={selected.ring === 0 ? 'Moderate' : selected.ring === 1 ? 'Growing' : 'Specialized'} />
                    </div>

                    {selected.skills?.length > 0 && (
                      <div className="mt-4">
                        <p className="text-[12px] text-white font-semibold mb-1.5">Key skills to develop</p>
                        <div className="space-y-1">
                          {selected.skills.map((s, i) => (
                            <div key={i} className="flex items-center gap-2 text-[12px]" style={{ color: C.text2 }}>
                              <span className="w-1.5 h-1.5 rounded-full" style={{ background: C.cyan }} />{s}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {selected.employers?.length > 0 && (
                      <div className="mt-4">
                        <p className="text-[12px] text-white font-semibold mb-1.5">Who hires for this</p>
                        <div className="flex flex-wrap gap-1.5">
                          {selected.employers.map((e, i) => (
                            <span key={i} className="text-[11px] text-white px-2 py-0.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}55` }}>{e}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mt-5 space-y-2">
                      {isTarget(selected) ? (
                        <Link to="/app/career-intelligence" data-testid="open-career-plan"
                          className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-sm font-bold text-white"
                          style={{ background: GRAD.cta, boxShadow: GLOW.cyan }}>
                          <Target className="w-4 h-4" /> Open Career Plan <ArrowRight className="w-4 h-4" />
                        </Link>
                      ) : (
                        <button onClick={() => setAsTarget(selected.title)} disabled={settingTarget} data-testid="set-as-target"
                          className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-sm font-bold text-white disabled:opacity-60"
                          style={{ background: GRAD.cta, boxShadow: GLOW.cyan }}>
                          {settingTarget ? <Loader2 className="w-4 h-4 animate-spin" /> : <Target className="w-4 h-4" />} Set as Career Target
                        </button>
                      )}
                      <button onClick={() => toggleCompare(selected)} data-testid="preview-compare"
                        className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-xs font-semibold transition-all"
                        style={{ color: compare.find(n => n.id === selected.id) ? C.cyan : C.text2, border: `1px solid ${compare.find(n => n.id === selected.id) ? C.cyan : C.border}66` }}>
                        <GitCompare className="w-3.5 h-3.5" /> {compare.find(n => n.id === selected.id) ? 'Added to compare' : 'Add to compare'}
                      </button>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <div className="rounded-2xl p-8 text-center" style={{ background: C.surface, border: `1px solid ${C.border}66` }}>
                  <Compass className="w-8 h-8 mx-auto mb-3" style={{ color: C.cyan }} />
                  <p className="text-white font-bold">Select a career</p>
                  <p className="text-sm mt-1" style={{ color: C.muted }}>Click any path on the map to explore it.</p>
                </div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        {/* RECOMMENDED */}
        <motion.div variants={fadeUp}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" style={{ color: C.cyan }} />
              <div>
                <h2 className="font-outfit font-bold text-white text-lg">Recommended for You</h2>
                <p className="text-[12px]" style={{ color: C.muted }}>Based on your major, interests, skills, and goals.</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {recommended.length === 0 && <p className="text-sm col-span-full" style={{ color: C.muted }}>No careers match this filter with the data on file.</p>}
            {recommended.map(node => {
              const b = fitBucket(node); const fc = FIT[b].color;
              return (
                <div key={node.id} className="rounded-2xl p-4 transition-all hover:-translate-y-0.5" data-testid={`rec-${node.id}`}
                  style={{ background: C.surface, border: `1px solid ${C.border}55` }}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-2" style={{ background: `${fc}18`, border: `1px solid ${fc}44` }}>
                    <BarChart3 className="w-4 h-4" style={{ color: fc }} />
                  </div>
                  <p className="text-white font-semibold text-[15px] leading-tight">{node.title}</p>
                  <span className="inline-block mt-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ color: fc, background: `${fc}18`, border: `1px solid ${fc}40` }}>{FIT[b].label}</span>
                  <p className="text-[12px] mt-2" style={{ color: C.text2 }}>{node.salary}</p>
                  <p className="text-[11.5px] mt-1 leading-snug line-clamp-2" style={{ color: C.muted }}>{node.description?.split('.')[0]}.</p>
                  <div className="flex items-center gap-1.5 mt-3">
                    <button onClick={() => { setResearch(null); setSelected(node); }} className="flex-1 text-[12px] font-semibold text-white py-1.5 rounded-lg" style={{ background: GRAD.cta }}>Explore</button>
                    <button onClick={() => toggleCompare(node)} className="text-[12px] py-1.5 px-2.5 rounded-lg" style={{ color: C.text2, border: `1px solid ${C.border}66` }}>Compare</button>
                    <button onClick={() => toggleSave(node)} className="py-1.5 px-2 rounded-lg" style={{ border: `1px solid ${C.border}66` }}><Bookmark className="w-3.5 h-3.5" style={saved.includes(node.id) ? { color: C.cyan, fill: C.cyan } : { color: C.text2 }} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* COMPARE + UNLOCK */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <motion.div variants={fadeUp}>
            <div className="rounded-2xl p-5 h-full" style={{ background: C.surface, border: `1px solid ${C.border}55` }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <GitCompare className="w-5 h-5" style={{ color: C.blue }} />
                  <div><h3 className="font-outfit font-bold text-white">Compare Careers</h3><p className="text-[11px]" style={{ color: C.muted }}>Select 2-3 careers to compare side by side.</p></div>
                </div>
                <button disabled={compare.length < 2} onClick={() => setShowCompare(true)} data-testid="compare-selected-btn"
                  className="text-[12px] font-bold text-white px-3 py-1.5 rounded-lg disabled:opacity-40" style={{ background: GRAD.cta }}>
                  Compare Selected ({compare.length})
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {compare.length === 0 && <p className="text-[12px]" style={{ color: C.muted }}>Add careers from the map or recommendations.</p>}
                {compare.map(n => (
                  <span key={n.id} className="flex items-center gap-1.5 text-[12px] text-white px-2.5 py-1 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}66` }}>
                    {n.title}<button onClick={() => toggleCompare(n)}><X className="w-3 h-3" style={{ color: C.muted }} /></button>
                  </span>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div variants={fadeUp}>
            <div className="rounded-2xl p-5 h-full" style={{ background: C.surface, border: `1px solid ${C.border}55` }}>
              <div className="flex items-center gap-2 mb-3">
                <Rocket className="w-5 h-5" style={{ color: C.gold }} />
                <div><h3 className="font-outfit font-bold text-white">Unlock More Career Paths</h3><p className="text-[11px]" style={{ color: C.muted }}>Small moves can open up even more careers.</p></div>
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                {unlocks.slice(0, 4).map((card, i) => {
                  const Icon = [Brain, GraduationCap, Award, BarChart3][i % 4];
                  return (
                    <Link to="/app/proof-builder" key={i} data-testid={`unlock-${i}`} className="rounded-xl p-3 transition-all hover:-translate-y-0.5"
                      style={{ background: `${card.color}0F`, border: `1px solid ${card.color}33` }}>
                      <Icon className="w-4 h-4 mb-1.5" style={{ color: card.color }} />
                      <p className="text-[12px] font-semibold text-white leading-tight">{card.title}</p>
                      <p className="text-[11px] mt-0.5" style={{ color: card.color }}>Opens {card.careers} more careers</p>
                    </Link>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </div>

        <p className="text-center text-[13px] italic pt-1" style={{ color: C.muted }}>“You don't have to have it all figured out. You just have to take the next step.”</p>
      </motion.div>

      {/* COMPARE MODAL */}
      <AnimatePresence>
        {showCompare && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(2,6,20,0.8)' }} onClick={() => setShowCompare(false)}>
            <motion.div initial={{ scale: 0.96, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 10 }} onClick={e => e.stopPropagation()}
              className="w-full max-w-3xl rounded-2xl p-6 overflow-auto max-h-[85vh]" style={{ background: C.surface, border: `1px solid ${C.cyan}55`, boxShadow: GLOW.hero }} data-testid="compare-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-outfit font-bold text-white text-lg">Compare Careers</h3>
                <button onClick={() => setShowCompare(false)}><X className="w-5 h-5" style={{ color: C.muted }} /></button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-[12.5px]">
                  <thead><tr style={{ color: C.muted }}>
                    <th className="py-2 pr-3 font-medium">Field</th>
                    {compare.map(n => <th key={n.id} className="py-2 px-3 text-white font-bold">{n.title}</th>)}
                  </tr></thead>
                  <tbody style={{ color: C.text2 }}>
                    {[
                      ['Fit', n => FIT[fitBucket(n)].label],
                      ['Early-career salary', n => n.salary],
                      ['Job growth', n => `${n.outlook} · ${n.outlookLabel}`],
                      ['Skills to develop', n => (n.skills || []).join(', ') || '-'],
                      ['Who hires', n => (n.employers || []).slice(0, 3).join(', ') || '-'],
                    ].map(([label, fn]) => (
                      <tr key={label} style={{ borderTop: `1px solid ${C.border}33` }}>
                        <td className="py-2.5 pr-3 font-medium" style={{ color: C.muted }}>{label}</td>
                        {compare.map(n => <td key={n.id} className="py-2.5 px-3">{fn(n)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CONFIRM TARGET CHANGE */}
      <AnimatePresence>
        {confirmChange && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(2,6,20,0.8)' }} onClick={() => setConfirmChange(null)}>
            <motion.div initial={{ scale: 0.96 }} animate={{ scale: 1 }} exit={{ scale: 0.96 }} onClick={e => e.stopPropagation()}
              className="w-full max-w-md rounded-2xl p-6" style={{ background: C.surface, border: `1px solid ${C.purple}66`, boxShadow: GLOW.hero }} data-testid="confirm-target-modal">
              <Star className="w-6 h-6 mb-2" style={{ color: C.gold }} />
              <h3 className="font-outfit font-bold text-white text-lg">Change your career target?</h3>
              <p className="text-[13px] mt-2" style={{ color: C.text2 }}>{confirmChange.message}</p>
              <div className="flex gap-2 mt-5">
                <button onClick={() => setConfirmChange(null)} className="flex-1 py-2.5 rounded-xl text-sm font-semibold" style={{ color: C.text2, border: `1px solid ${C.border}66` }}>Keep current</button>
                <button onClick={() => setAsTarget(confirmChange.title, true)} disabled={settingTarget} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-60" style={{ background: GRAD.cta }}>
                  {settingTarget ? 'Switching…' : 'Yes, switch target'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AppLayout>
  );
}

function Stat({ icon: Icon, color, label, value, sub }) {
  return (
    <div className="rounded-xl p-2.5" style={{ background: C.surface2, border: `1px solid ${C.border}44` }}>
      <Icon className="w-3.5 h-3.5 mb-1" style={{ color }} />
      <p className="text-[9px]" style={{ color: C.muted }}>{label}</p>
      <p className="text-[12px] font-bold text-white leading-tight">{value}</p>
      {sub && <p className="text-[8px]" style={{ color: C.muted }}>{sub}</p>}
    </div>
  );
}
